<template>
    <div class="crud-area">
        <div class="container-fluid">
            <div class="crud-header mb-4">
                <h2 class="crud-title">Candidaturas de Voluntários</h2>
                <p class="crud-subtitle">Gerir os candidatos e as suas candidaturas aos projectos</p>
            </div>

            <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary"></div>
            </div>
            <div v-if="error" class="alert-error mb-3"><i class="ri-error-warning-line"></i> {{ error }}</div>

            <div v-if="!loading" class="crud-card">
                <div class="table-responsive">
                    <table class="crud-table">
                        <thead>
                            <tr>
                                <th>Candidato</th>
                                <th>Contacto</th>
                                <th>Projecto</th>
                                <th>CV</th>
                                <th>Estado</th>
                                <th class="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="items.length === 0">
                                <td colspan="6" class="text-center text-muted py-4">Sem candidaturas registadas.</td>
                            </tr>
                            <tr v-for="item in items" :key="item.id">
                                <td>
                                    <div class="d-flex flex-column">
                                        <span class="fw-bold text-dark">{{ item.name }}</span>
                                        <span class="small text-muted">{{ item.email }}</span>
                                    </div>
                                </td>
                                <td>{{ item.phone }}</td>
                                <td><span class="text-primary fw-600">{{ item.project?.title_pt || '—' }}</span></td>
                                <td>
                                    <a :href="item.cvUrl" target="_blank" class="btn-cv-link">
                                        <i class="ri-file-pdf-line"></i> Ver CV
                                    </a>
                                </td>
                                <td>
                                    <span :class="['badge-status', item.status.toLowerCase()]">
                                        {{ formatStatus(item.status) }}
                                    </span>
                                </td>
                                <td class="text-end">
                                    <div class="d-flex justify-content-end gap-2" v-if="item.status === 'PENDING'">
                                        <button class="btn-action-sm approve" @click="handleStatusClick(item, 'APPROVED')" :disabled="processing === item.id">
                                            <i class="ri-check-line"></i> Aceitar
                                        </button>
                                        <button class="btn-action-sm reject" @click="handleStatusClick(item, 'REJECTED')" :disabled="processing === item.id">
                                            <i class="ri-close-line"></i> Rejeitar
                                        </button>
                                    </div>
                                    <span v-else class="small text-muted italic">Processado em {{ formatDate(item.updatedAt) }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Confirm Status Modal -->
        <div 
            class="modal fade" 
            :class="{ show: showConfirmModal, 'd-block': showConfirmModal }" 
            tabindex="-1" 
            role="dialog" 
            style="background: rgba(0, 0, 0, 0.5)" 
            v-if="showConfirmModal"
        >
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">
                            <i :class="confirmStatus === 'APPROVED' ? 'ri-check-line text-success' : 'ri-close-line text-danger'" class="me-2"></i>
                            {{ confirmStatus === 'APPROVED' ? 'Aceitar Candidatura' : 'Rejeitar Candidatura' }}
                        </h5>
                        <button type="button" class="btn-close" @click="closeConfirmModal" :disabled="isConfirming"></button>
                    </div>
                    <div class="modal-body text-start">
                        <p class="mb-2" style="font-size: 16px; color: #444">
                            Tem a certeza que deseja <strong>{{ confirmStatus === 'APPROVED' ? 'aceitar' : 'rejeitar' }}</strong> a candidatura de <strong class="text-primary">{{ selectedCandidate?.name }}</strong>?
                        </p>
                        <div class="alert mt-3 mb-0 d-flex align-items-center" :class="confirmStatus === 'APPROVED' ? 'alert-success' : 'alert-danger'" style="font-size: 14px; padding: 10px;">
                            <i class="ri-mail-send-line me-2" style="font-size: 18px;"></i>
                            Um email de notificação será enviado automaticamente para o candidato.
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary px-4 fw-600" @click="closeConfirmModal" :disabled="isConfirming">
                            Cancelar
                        </button>
                        <button 
                            type="button" 
                            class="btn px-4 fw-600 text-white" 
                            :class="confirmStatus === 'APPROVED' ? 'btn-success' : 'btn-danger'" 
                            @click="executeStatusUpdate"
                            :disabled="isConfirming"
                        >
                            <i v-if="isConfirming" class="ri-loader-4-line spin me-1"></i>
                            {{ isConfirming ? 'A processar...' : 'Confirmar' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import volunteerService from '../../services/volunteerService';

export default {
    name: 'VolunteersContent',
    data() {
        return {
            items: [],
            loading: true,
            processing: null,
            error: '',
            showConfirmModal: false,
            selectedCandidate: null,
            confirmStatus: null,
            isConfirming: false,
        };
    },
    async mounted() {
        this.load();
    },
    methods: {
        async load() {
            this.loading = true;
            this.error = '';
            try {
                this.items = await volunteerService.getVolunteers();
            } catch (err) {
                this.error = 'Erro ao carregar candidaturas de voluntários.';
            } finally {
                this.loading = false;
            }
        },
        handleStatusClick(item, status) {
            this.selectedCandidate = item;
            this.confirmStatus = status;
            this.showConfirmModal = true;
        },
        closeConfirmModal() {
            if (this.isConfirming) return;
            this.showConfirmModal = false;
            this.selectedCandidate = null;
            this.confirmStatus = null;
        },
        async executeStatusUpdate() {
            if (!this.selectedCandidate || !this.confirmStatus) return;

            const item = this.selectedCandidate;
            const status = this.confirmStatus;

            this.isConfirming = true;
            this.processing = item.id;
            try {
                await volunteerService.updateStatus(item.id, status);
                await this.load();
                this.showConfirmModal = false;
                this.selectedCandidate = null;
                this.confirmStatus = null;
            } catch (err) {
                this.error = 'Erro ao atualizar estado da candidatura.';
            } finally {
                this.isConfirming = false;
                this.processing = null;
            }
        },
        formatStatus(status) {
            const statusMap = {
                'PENDING': 'Pendente',
                'APPROVED': 'Aceito',
                'REJECTED': 'Rejeitado'
            };
            return statusMap[status] || status;
        },
        formatDate(dateStr) {
            return new Date(dateStr).toLocaleDateString('pt-PT');
        }
    },
};
</script>

<style lang="scss" scoped>
@import '../Dashboards/GgmfDashboard/ggmf-card';

.badge-status {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    display: inline-block;

    &.pending { background: #fff3e0; color: #ef6c00; border: 1px solid #ffe0b2; }
    &.approved { background: #e8f5e9; color: #2e7d32; border: 1px solid #c8e6c9; }
    &.rejected { background: #ffebee; color: #c62828; border: 1px solid #ffcdd2; }
}

.btn-cv-link {
    color: #ef4444;
    text-decoration: none;
    font-weight: 600;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 4px;
    &:hover { color: #dc2626; text-decoration: underline; }
}

.btn-action-sm {
    padding: 6px 14px;
    border-radius: 8px;
    border: none;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;

    &.approve {
        background: #10b981;
        color: white;
        &:hover { background: #059669; }
    }

    &.reject {
        background: #64748b;
        color: white;
        &:hover { background: #475569; }
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
